/**
 * CasePage：命例管理页
 * - 分类栏：横向 chips（点击过滤）+「修改分类」按钮（编辑模式：改名/删除/新增分类）
 * - 命例列表：名称、性别、生辰（阳历）、地点、备注；点击仅选中，再经底部按钮排盘/修改/删除
 * - 底部操作：+添加 / 修改 / 删除 / 排盘
 */
import { useEffect, useMemo, useState } from 'react';
import { caseSummary, DEFAULT_CATEGORY } from '@shared/engine/chart-store';
import { useChartStore } from '../state/store';
import ZizhanDialog from './ZizhanDialog';

export default function CasePage() {
  const cases = useChartStore((s) => s.cases);
  const categories = useChartStore((s) => s.categories);
  const selectedCaseId = useChartStore((s) => s.selectedCaseId);
  const selectCase = useChartStore((s) => s.selectCase);
  const removeCase = useChartStore((s) => s.removeCase);
  const setCategories = useChartStore((s) => s.setCategories);
  const renameCategory = useChartStore((s) => s.renameCategory);
  const deleteCategory = useChartStore((s) => s.deleteCategory);
  const createChart = useChartStore((s) => s.createChart);
  const syncCloud = useChartStore((s) => s.syncCloud);

  // 进入命例页即拉一次云端（内部已登录校验 + 并发守卫；失败保留本地）
  useEffect(() => {
    void syncCloud();
  }, [syncCloud]);

  const [filter, setFilter] = useState('全部');
  const [editCats, setEditCats] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [renaming, setRenaming] = useState<Record<string, string>>({});
  const [confirmDel, setConfirmDel] = useState(false);
  const [zizhanOpen, setZizhanOpen] = useState(false);

  const filtered = useMemo(
    () => (filter === '全部' ? cases : cases.filter((c) => c.category === filter)),
    [cases, filter],
  );
  const selected = cases.find((c) => c.chartId === selectedCaseId) ?? null;

  function onAdd() {
    window.location.hash = '#/case-form';
  }

  function onEdit() {
    if (selected) window.location.hash = `#/case-form?edit=${selected.chartId}`;
  }

  async function onDelete() {
    if (!selected) return;
    if (!confirmDel) {
      setConfirmDel(true);
      setTimeout(() => setConfirmDel(false), 3000);
      return;
    }
    setConfirmDel(false);
    await removeCase(selected.chartId);
  }

  async function onPaiPan() {
    if (!selected) return;
    await createChart(selected.input);
    window.location.hash = '#/chart';
  }

  async function onRename(cat: string) {
    const v = (renaming[cat] ?? '').trim();
    if (v && v !== cat) await renameCategory(cat, v);
    setRenaming((m) => {
      const n = { ...m };
      delete n[cat];
      return n;
    });
  }

  async function onAddCategory() {
    const v = newCat.trim();
    if (!v || categories.includes(v)) return;
    await setCategories([...categories, v]);
    setNewCat('');
  }

  const btnCls = 'flex-1 whitespace-nowrap rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40';
  const subCls = 'flex-1 whitespace-nowrap rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40';

  return (
    <div className="case-page-fill flex flex-col gap-3">
      {/* 分类栏 */}
      <div className="shrink-0 rounded-lg bg-white p-3 shadow">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">分类</span>
          <button
            type="button"
            onClick={() => {
              setEditCats((v) => !v);
              setRenaming({});
              setNewCat('');
            }}
            className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-600 hover:bg-slate-200"
          >
            {editCats ? '完成' : '修改分类标签'}
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {!editCats && (
            <button
              type="button"
              onClick={() => setFilter('全部')}
              className={`rounded-full px-3 py-1 text-xs ${filter === '全部' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600'}`}
            >
              全部
            </button>
          )}
          {categories.map((cat) =>
            editCats ? (
              <span key={cat} className="flex items-center gap-1 rounded-full bg-slate-100 py-1 pl-3 pr-1 text-xs text-slate-600">
                <input
                  value={renaming[cat] ?? cat}
                  onChange={(e) => setRenaming((m) => ({ ...m, [cat]: e.target.value }))}
                  className="w-16 rounded border border-slate-300 px-1 py-0.5 text-xs"
                />
                <button type="button" onClick={() => void onRename(cat)} className="rounded bg-slate-200 px-1.5 py-0.5 hover:bg-slate-300">
                  ✓
                </button>
                {cat !== DEFAULT_CATEGORY && (
                  <button type="button" onClick={() => void deleteCategory(cat)} className="rounded bg-red-100 px-1.5 py-0.5 text-red-600 hover:bg-red-200">
                    ✕
                  </button>
                )}
              </span>
            ) : (
              <button
                key={cat}
                type="button"
                onClick={() => setFilter(cat)}
                className={`rounded-full px-3 py-1 text-xs ${filter === cat ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600'}`}
              >
                {cat}
              </button>
            ),
          )}
          {editCats && (
            <span className="flex items-center gap-1 rounded-full bg-white py-1 pl-3 pr-1 ring-1 ring-slate-300">
              <input
                value={newCat}
                onChange={(e) => setNewCat(e.target.value)}
                placeholder="新分类"
                className="w-16 text-xs outline-none"
              />
              <button type="button" onClick={() => void onAddCategory()} className="rounded bg-slate-700 px-1.5 py-0.5 text-white hover:bg-slate-600">
                +
              </button>
            </span>
          )}
        </div>
      </div>

      {/* 命例列表（唯一内部滚动区：操作栏固定不随列表滚动） */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="rounded-lg bg-white px-4 py-10 text-center text-sm text-slate-400 shadow">
            {cases.length === 0 ? '暂无命例，点击下方「+ 添加」录入第一个命例' : '该分类下暂无命例'}
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {filtered.map((r) => {
              const active = r.chartId === selectedCaseId;
              return (
                <li
                  key={r.chartId}
                  onClick={() => {
                    // 行点击＝仅选中（高亮）；打开命盘由底部「排盘」按钮触发，修改/删除同样基于选中态
                    selectCase(r.chartId);
                  }}
                  className={`cursor-pointer rounded-lg bg-white px-3 py-2 shadow transition-colors ${
                    active ? 'ring-2 ring-slate-700' : 'hover:bg-slate-50'
                  }`}
                >
                  {/* 单行：名称 · 出生时间 · 性别 · 地点 · 分类（节省纵向空间） */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{r.name}</span>
                    {/* 生辰状态：每行都显示，位于姓名与出生信息之间；黑字无底色，与周围文字风格一致 */}
                    <span className="shrink-0 text-xs text-slate-800">{r.hideBirth ? '隐藏生辰' : '显示生辰'}</span>
                    <span className="shrink-0 text-xs text-slate-500">{caseSummary(r)}</span>
                    <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                      {r.category}
                    </span>
                  </div>
                  {r.remark && <div className="mt-0.5 truncate text-xs text-slate-400">备注：{r.remark}</div>}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* 底部操作（固定页底，不随列表滚动） */}
      <div className="flex shrink-0 gap-2 rounded-lg bg-white p-3 shadow">
        <button type="button" onClick={onAdd} className={btnCls}>
          + 添加
        </button>
        <button type="button" onClick={onEdit} disabled={!selected} className={subCls}>
          修改
        </button>
        <button
          type="button"
          onClick={() => void onDelete()}
          disabled={!selected}
          className={`${subCls} ${confirmDel ? '!bg-red-600 !text-white !border-red-600' : ''}`}
        >
          {confirmDel ? '确认删除？' : '删除'}
        </button>
        <button type="button" onClick={() => void onPaiPan()} disabled={!selected} className={btnCls}>
          排盘
        </button>
        <button type="button" onClick={() => setZizhanOpen(true)} className={btnCls}>
          紫占
        </button>
      </div>

      {zizhanOpen && <ZizhanDialog onClose={() => setZizhanOpen(false)} />}
    </div>
  );
}
